import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Card, Input, Select, Button, Space, Tag, Drawer, Form,
  InputNumber, Radio, Switch, message, Row, Col, Statistic, Progress,
  Typography, Tooltip, Popconfirm, Divider,
} from 'antd';
import {
  SearchOutlined, DownloadOutlined, SafetyCertificateOutlined,
  ReloadOutlined, EditOutlined, GlobalOutlined,
} from '@ant-design/icons';
import { adminAPI } from '../services/api';
import { useLanguage } from '../store/LanguageContext';
import { Tool, AvailabilityCN, ComplianceTier, ComplianceStatus, ComplianceStats } from '../types';
import { availabilityCNAdminOptions } from '../constants/availabilityCN';

const { Title, Text } = Typography;
const { Option } = Select;

const availabilityCNOptions = availabilityCNAdminOptions;

const complianceTierOptionsBase: { value: ComplianceTier; key: string }[] = [
  { value: 'T1_full', key: 'tier.T1_full' },
  { value: 'T2_partial', key: 'tier.T2_partial' },
  { value: 'T3_none', key: 'tier.T3_none' },
  { value: 'pending', key: 'tier.pending' },
];

const statusOptionsBase: { value: ComplianceStatus; key: string }[] = [
  { value: 'passed', key: 'status.passed' },
  { value: 'failed', key: 'status.failed' },
  { value: 'in_progress', key: 'status.in_progress' },
  { value: 'not_started', key: 'status.not_started' },
];

const dimensionFieldKeys = [
  { key: 'data_residency', labelKey: 'dim.data_residency' },
  { key: 'privacy', labelKey: 'dim.privacy' },
  { key: 'encryption', labelKey: 'dim.encryption' },
  { key: 'certification', labelKey: 'dim.certification' },
  { key: 'content_moderation', labelKey: 'dim.content_moderation' },
  { key: 'audit_logging', labelKey: 'dim.audit_logging' },
  { key: 'sla_support', labelKey: 'dim.sla_support' },
  { key: 'export_control', labelKey: 'dim.export_control' },
];

const AdminToolsPage: React.FC = () => {
  const { t } = useLanguage();

  const complianceTierOptions = complianceTierOptionsBase.map(o => ({ ...o, label: t(o.key) }));
  const statusOptions = statusOptionsBase.map(o => ({ ...o, label: t(o.key) }));
  const dimensionFields = dimensionFieldKeys.map(o => ({ ...o, label: t(o.labelKey) }));

  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState('');
  const [filterAvailability, setFilterAvailability] = useState<string>('');
  const [filterTier, setFilterTier] = useState<string>('');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingTool, setEditingTool] = useState<Tool | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const [stats, setStats] = useState<ComplianceStats | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [batchField, setBatchField] = useState<string>('availability_cn');
  const [batchValue, setBatchValue] = useState<string>('');

  const fetchTools = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page: pagination.page, limit: pagination.pageSize };
      if (search) params.search = search;
      if (filterAvailability) params.availability_cn = filterAvailability;
      if (filterTier) params.compliance_tier = filterTier;
      const res = await adminAPI.getTools(params);
      setTools(res.data.tools || []);
      const p = res.data.pagination;
      setPagination((prev) => ({ ...prev, total: p?.total || 0 }));
    } catch (error) {
      message.error(t('admin.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, search, filterAvailability, filterTier]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await adminAPI.getStats();
      setStats(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchTools(); }, [fetchTools]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleEdit = (tool: Tool) => {
    setEditingTool(tool);
    form.setFieldsValue({
      availability_cn: tool.availability_cn,
      cn_url: tool.cn_url,
      cn_icp: tool.cn_icp,
      cn_app_store: tool.cn_app_store,
      cn_wechat_mini: tool.cn_wechat_mini,
      supports_invoice: tool.supports_invoice,
      supports_contract: tool.supports_contract,
      supports_on_prem: tool.supports_on_prem,
      procurement_channel: tool.procurement_channel,
      procurement_contact: tool.procurement_contact,
      min_seats: tool.min_seats,
      // 八维
      ...dimensionFieldKeys.reduce((acc, d) => ({
        ...acc,
        [`dim_${d.key}_score`]: (tool as any)[`dim_${d.key}_score`] || 0,
        [`dim_${d.key}_status`]: (tool as any)[`dim_${d.key}_status`] || 'not_started',
        [`dim_${d.key}_evidence`]: (tool as any)[`dim_${d.key}_evidence`] || '',
      }), {}),
    });
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await adminAPI.patchCompliance(editingTool!.id, values);
      message.success(t('admin.updateSuccess'));
      setDrawerOpen(false);
      setEditingTool(null);
      fetchTools();
      fetchStats();
    } catch (error: any) {
      if (error.errorFields) return; // form validation
      message.error(t('admin.updateFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await adminAPI.exportCompliance({
        availability_cn: filterAvailability || undefined,
        compliance_tier: filterTier || undefined,
      });
      const dataStr = JSON.stringify(res.data.tools || [], null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compliance-export-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      message.success(t('admin.exportSuccess'));
    } catch {
      message.error(t('admin.exportFailed'));
    }
  };

  const handleBatch = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('admin.selectToolsFirst'));
      return;
    }
    if (!batchValue) {
      message.warning(t('admin.setBatchValue'));
      return;
    }
    try {
      await adminAPI.batchUpdate(
        selectedRowKeys as number[],
        { [batchField]: batchField.startsWith('supports_') ? batchValue === 'true' : batchValue }
      );
      message.success(t('admin.batchSuccess', { count: String(selectedRowKeys.length) }));
      setSelectedRowKeys([]);
      fetchTools();
      fetchStats();
    } catch {
      message.error(t('admin.batchFailed'));
    }
  };

  const tierColor = (tier: string) =>
    tier === 'T1_full' ? 'green' : tier === 'T2_partial' ? 'blue' : tier === 'T3_none' ? 'default' : 'orange';

  const columns = [
    {
      title: t('admin.colName'), dataIndex: 'name', width: 180,
      render: (name: string, record: Tool) => (
        <Space>
          {record.logo && <img src={record.logo} alt="" style={{ width: 24, height: 24, borderRadius: 4 }} />}
          <Text strong>{name}</Text>
        </Space>
      ),
    },
    {
      title: t('admin.colAvailability'), dataIndex: 'availability_cn', width: 110,
      render: (v: string) => {
        const opt = availabilityCNOptions.find(o => o.value === v);
        return opt ? <Tag>{opt.label}</Tag> : <Tag>{t('common.unknown')}</Tag>;
      },
    },
    {
      title: t('admin.colTier'), dataIndex: 'compliance_tier', width: 110,
      render: (v: string) => <Tag color={tierColor(v)}>{complianceTierOptions.find(o => o.value === v)?.label || v}</Tag>,
    },
    {
      title: t('admin.colScore'), dataIndex: 'compliance_score', width: 120, sorter: true,
      render: (v: number) => (
        <Progress
          percent={v || 0}
          size="small"
          strokeColor={v >= 80 ? '#52c41a' : v >= 50 ? '#1890ff' : '#ff4d4f'}
          format={(p) => `${p}`}
        />
      ),
    },
    {
      title: t('admin.colInvoice'), dataIndex: 'supports_invoice', width: 60,
      render: (v: boolean) => v ? <Tag color="cyan">✓</Tag> : '-',
    },
    {
      title: t('admin.colContract'), dataIndex: 'supports_contract', width: 60,
      render: (v: boolean) => v ? <Tag color="cyan">✓</Tag> : '-',
    },
    {
      title: t('admin.colOnPrem'), dataIndex: 'supports_on_prem', width: 60,
      render: (v: boolean) => v ? <Tag color="cyan">✓</Tag> : '-',
    },
    {
      title: t('admin.colActions'), width: 80,
      render: (_: any, record: Tool) => (
        <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>{t('admin.edit')}</Button>
      ),
    },
  ];

  return (
    <div className="page-container" style={{ padding: '24px 32px' }}>
      <Title level={3}><SafetyCertificateOutlined /> {t('admin.title')}</Title>

      {/* 统计卡片 */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={4}>
            <Card size="small"><Statistic title={t('admin.totalTools')} value={stats.total} /></Card>
          </Col>
          <Col span={4}>
            <Card size="small"><Statistic title={t('admin.t1Count')} value={stats.by_tier?.T1_full || 0} valueStyle={{ color: '#52c41a' }} /></Card>
          </Col>
          <Col span={4}>
            <Card size="small"><Statistic title={t('admin.t2Count')} value={stats.by_tier?.T2_partial || 0} valueStyle={{ color: '#1890ff' }} /></Card>
          </Col>
          <Col span={4}>
            <Card size="small"><Statistic title={t('admin.t3Count')} value={stats.by_tier?.T3_none || 0} valueStyle={{ color: '#999' }} /></Card>
          </Col>
          <Col span={4}>
            <Card size="small"><Statistic title={t('admin.cnNative')} value={stats.by_availability?.cn_native || 0} valueStyle={{ color: '#f5222d' }} /></Card>
          </Col>
          <Col span={4}>
            <Card size="small"><Statistic title={t('admin.directAccess')} value={stats.by_availability?.direct || 0} valueStyle={{ color: '#52c41a' }} /></Card>
          </Col>
        </Row>
      )}

      {/* 筛选栏 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder={t('admin.searchPlaceholder')}
            prefix={<SearchOutlined />}
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={() => { setPagination(p => ({ ...p, page: 1 })); fetchTools(); }}
            style={{ width: 220 }}
          />
          <Select
            placeholder={t('admin.colAvailability')}
            allowClear
            value={filterAvailability || undefined}
            onChange={(v) => { setFilterAvailability(v || ''); setPagination(p => ({ ...p, page: 1 })); }}
            style={{ width: 150 }}
          >
            {availabilityCNOptions.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
          </Select>
          <Select
            placeholder={t('admin.colTier')}
            allowClear
            value={filterTier || undefined}
            onChange={(v) => { setFilterTier(v || ''); setPagination(p => ({ ...p, page: 1 })); }}
            style={{ width: 150 }}
          >
            {complianceTierOptions.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
          </Select>
          <Button icon={<ReloadOutlined />} onClick={() => { fetchTools(); fetchStats(); }}>{t('admin.refresh')}</Button>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>{t('admin.exportJson')}</Button>
        </Space>
      </Card>

      {/* 批量操作栏 */}
      {selectedRowKeys.length > 0 && (
        <Card size="small" style={{ marginBottom: 16, background: '#e6f7ff' }}>
          <Space>
            <Text>{t('admin.selectedCount', { count: selectedRowKeys.length })}</Text>
            <Select value={batchField} onChange={setBatchField} style={{ width: 150 }}>
              <Option value="availability_cn">{t('admin.colAvailability')}</Option>
              <Option value="compliance_tier">{t('admin.colTier')}</Option>
              <Option value="supports_invoice">{t('admin.labelInvoice')}</Option>
              <Option value="supports_contract">{t('admin.labelContract')}</Option>
              <Option value="supports_on_prem">{t('admin.labelOnPrem')}</Option>
            </Select>
            {batchField === 'availability_cn' ? (
              <Select value={batchValue} onChange={setBatchValue} style={{ width: 150 }} placeholder={t('admin.selectValue')}>
                {availabilityCNOptions.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
              </Select>
            ) : batchField === 'compliance_tier' ? (
              <Select value={batchValue} onChange={setBatchValue} style={{ width: 150 }} placeholder={t('admin.selectValue')}>
                {complianceTierOptions.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
              </Select>
            ) : (
              <Select value={batchValue} onChange={setBatchValue} style={{ width: 100 }} placeholder={t('admin.selectValue')}>
                <Option value="true">{t('admin.yes')}</Option>
                <Option value="false">{t('admin.no')}</Option>
              </Select>
            )}
            <Popconfirm title={t('admin.batchConfirm', { count: selectedRowKeys.length })} onConfirm={handleBatch}>
              <Button type="primary">{t('admin.batchUpdate')}</Button>
            </Popconfirm>
            <Button onClick={() => setSelectedRowKeys([])}>{t('admin.cancelSelection')}</Button>
          </Space>
        </Card>
      )}

      {/* 工具表格 */}
      <Table
        rowKey="id"
        columns={columns}
        dataSource={tools}
        loading={loading}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onChange: (p, ps) => setPagination({ page: p, pageSize: ps || 20, total: pagination.total }),
          showTotal: (total) => t('admin.totalItems', { total }),
          showSizeChanger: true,
        }}
        scroll={{ x: 900 }}
        size="middle"
      />

      {/* 编辑抽屉 */}
      <Drawer
        title={editingTool ? t('admin.editComplianceFor', { name: editingTool.name }) : t('admin.editCompliance')}
        width={640}
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditingTool(null); }}
        extra={
          <Space>
            <Button onClick={() => { setDrawerOpen(false); setEditingTool(null); }}>{t('admin.cancel')}</Button>
            <Button type="primary" loading={submitting} onClick={handleSave}>{t('admin.save')}</Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Title level={5}><GlobalOutlined /> {t('admin.sectionCnAvail')}</Title>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="availability_cn" label={t('admin.labelAvailability')}>
                <Select>{availabilityCNOptions.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}</Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="cn_url" label={t('admin.labelCnUrl')}><Input placeholder={t('admin.cnUrlPlaceholder')} /></Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="cn_icp" label={t('admin.labelIcp')}><Input placeholder={t('admin.cnIcpPlaceholder')} /></Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="cn_app_store" label={t('admin.labelAppStore')} valuePropName="checked"><Switch /></Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="cn_wechat_mini" label={t('admin.labelWechat')} valuePropName="checked"><Switch /></Form.Item>
            </Col>
          </Row>

          <Divider />
          <Title level={5}><SafetyCertificateOutlined /> {t('admin.complianceDimensions')}</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            {t('admin.autoCalcNote')}
          </Text>

          {dimensionFields.map(({ key, label }) => (
            <Card key={key} size="small" style={{ marginBottom: 12 }} title={label}>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name={`dim_${key}_score`} label={t('admin.dimScore')}>
                    <InputNumber min={0} max={100} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={`dim_${key}_status`} label={t('admin.dimStatus')}>
                    <Select>{statusOptions.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}</Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={`dim_${key}_evidence`} label={t('admin.dimEvidence')}>
                    <Input placeholder={t('admin.dimEvidencePlaceholder')} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          ))}

          <Divider />
          <Title level={5}>{t('admin.sectionEnterprise')}</Title>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="supports_invoice" label={t('admin.labelInvoice')} valuePropName="checked"><Switch /></Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="supports_contract" label={t('admin.labelContract')} valuePropName="checked"><Switch /></Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="supports_on_prem" label={t('admin.labelOnPrem')} valuePropName="checked"><Switch /></Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="procurement_channel" label={t('admin.labelChannel')}>
                <Select>
                  <Option value="direct">{t('admin.channelDirect')}</Option>
                  <Option value="reseller">{t('admin.channelReseller')}</Option>
                  <Option value="cloud_marketplace">{t('admin.channelCloud')}</Option>
                  <Option value="opensource">{t('admin.channelOpensource')}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="procurement_contact" label={t('admin.labelContact')}><Input placeholder={t('admin.labelContactPlaceholder')} /></Form.Item>
            </Col>
          </Row>
          <Form.Item name="min_seats" label={t('admin.labelMinSeats')}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default AdminToolsPage;
